import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  GenericAbortSignal,
  ResponseType,
} from 'axios';
import { isArrayOfStrings, mapObjEmptyStrToNull } from './utils';
import { Logger, MessageBox } from '../context/MessageContext';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const procResponseData = (data: any) => {
  if (data == null) return ['empty response'];
  let lines: string[] = [];
  if (Array.isArray(data)) {
    lines = lines.concat(data.map((r) => r.toString()));
  } else if (typeof data === 'object') {
    // asp.net core 파라미터 바인딩 실패할 경우 등
    if ('Title' in data) lines.push(data.Title.toString());
    if ('Errors' in data) lines.push(JSON.stringify(data.Errors));
  } else if (typeof data === 'string') {
    lines.push(data);
  }
  if (lines.length === 0) {
    lines.push(JSON.stringify(data));
  }
  return lines;
};

export const getAxiosErrorMsgs = (
  msg: string | string[] | AxiosError,
  fallbackMsg?: string,
) => {
  let lines: string[] = [];
  if (typeof msg === 'string') {
    if (msg) lines.push(msg);
  } else if (isArrayOfStrings(msg)) {
    lines = msg;
  } else {
    // console.log(msg)
    // AxiosError
    if (msg.response) {
      const { data } = msg.response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errs = (data as any)?.errors ?? data;
      if (errs) {
        lines = lines.concat(procResponseData(errs));
      } else if (msg.response.statusText) {
        lines.push(msg.response.statusText);
      }
    } else {
      // msg 가 string도 아니고 AxiosError도 아닐때 (성공시 res.data를 넘기거나 하면)
      lines = lines.concat(procResponseData(msg));
    }
    if (lines.length === 0 && msg.message) {
      lines.push(msg.message);
    }
  }
  return lines.length ? lines : [fallbackMsg ?? 'unknown response'];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidData = any;

export interface CallAxiosArgs {
  axiosInstance: AxiosInstance;
  m: MessageBox;
  logger: Logger | null;
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any;
  confirmMsg?: string | null;
  whenInvalidMsg?: string | null;
  onSuccess?: (data: ValidData, res: AxiosResponse) => void;
  onError?: (errMsgs: string[], err: AxiosError) => void;
  onBegin?: () => void;
  contentType?: string;
  responseType?: ResponseType;
  isGet?: boolean; // 아니면 post
  title?: string;
  signal?: GenericAbortSignal | undefined;
  queryParams?: any; // Post 요청서 query로 전달하고 싶을때 (Get에선 사용불가)
}

export const callAxios = async ({
  axiosInstance,
  m,
  logger,
  url,
  params,
  confirmMsg,
  whenInvalidMsg,
  onSuccess,
  onError,
  onBegin,
  contentType,
  responseType,
  isGet,
  title,
  signal,
  queryParams
}: CallAxiosArgs) => {
  onBegin?.();

  if (confirmMsg) {
    if (!(await m.confirm?.(`${confirmMsg}?`))) return;
  }

  if (whenInvalidMsg) {
    m.alert(whenInvalidMsg);
    return;
  }
  
  const ajaxTimerId = m.showAjaxLoader();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const axiosCfg0: AxiosRequestConfig<any> = {
    method: isGet ? 'get' : 'post',
    url,
    signal,
  };

  // empty stirng as null (only 1 depth)
  const paramsFixed = mapObjEmptyStrToNull(params);

  if (isGet && !!queryParams) {
    alert("GET에서는 queryParams 사용 불가");
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const axiosCfg: AxiosRequestConfig<any> = isGet
    ? { ...axiosCfg0, params: paramsFixed, paramsSerializer: { indexes: null } }
    : {
      ...axiosCfg0,
      data: paramsFixed,
      params: !queryParams ? undefined : mapObjEmptyStrToNull(queryParams),
      headers: {
        'Content-Type': contentType ?? 'application/json',
      },
      responseType,
    };

  const logInfo = title || !isGet;
  if (logInfo) {
    // 요청 title 있으면 로그 기록
    logger?.addLog('info', title ?? url, '요청');
  }
  const logHeader = `응답${title ? `(${title})` : ''}`;

  // 디버깅: 실제 요청 URL 확인
  const fullUrl = axiosInstance.defaults.baseURL 
    ? `${axiosInstance.defaults.baseURL}${url}`
    : url;
  console.log('🔧 Axios 요청 URL:', fullUrl);
  console.log('🔧 Axios Instance baseURL:', axiosInstance.defaults.baseURL);

  return axiosInstance(axiosCfg)
    .then((res) => {
      m.hideAjaxLoader(ajaxTimerId);

      if (res.data.warnings?.length) {
        logger?.addLogs('warning', res.data.warnings, logHeader);
      } else if (logInfo) {
        logger?.addLog('info', 'OK', logHeader);
      }

      // 서버가 Result 타입을 바로 리턴한 경우, Result 타입이 fail이면 에러로 처리해야.
      // => Result타입을 바로 리턴하지 말고 ToIResult로 리턴해야. 일단은 안전망 차원에서.
      if (res.data.errors?.length && res.data.isSucc === false) {
        var errMsgs = res.data.errors;
        // 로깅서 한줄로 나오게 join
        logger?.addLogs('error', [errMsgs.join('\n')], logHeader);
        onError ? onError(errMsgs, null!) : m.alert(errMsgs, 'error');
        console.warn('Do not return Result type directly')
      }

      // TODO auxdata같이받을수있게
      const data = res.data.data === undefined ? res.data : res.data.data;
      // console.log(res);
      return onSuccess ? onSuccess(data, res) : m.alert(data, 'ok');
    })
    .catch(async (err) => {
      m.hideAjaxLoader(ajaxTimerId);

      // 다른 페이지로 옮기는 등으로 기 요청 취소면 그냥 넘어감
      if (err.code === 'ERR_CANCELED') {
        console.log(err)
        return;
      }

      if (responseType === 'blob') {
        return m.alert(await err.response.data.text());
      }
      const errMsgs = getAxiosErrorMsgs(err, 'error');
      // 로깅서 한줄로 나오게 join
      logger?.addLogs('error', [errMsgs.join('\n')], logHeader);
      return onError ? onError(errMsgs, err) : m.alert(errMsgs, 'error');
    });
};

export const callAxiosGet = (args: Omit<CallAxiosArgs, 'isGet'>) =>
  callAxios({ ...args, isGet: true });

const extractDownloadFilenameWithExt = (response: AxiosResponse) => {
  const disposition = response.headers['content-disposition'];
  const fileName = decodeURIComponent(
    disposition
      .split(';')[2]
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Disposition
      // https://stackoverflow.com/questions/40939380/how-to-get-file-name-from-content-disposition
      .match(/filename\*=UTF-8''([\w%\-\.]+)(?:; ?|$)/i)[1],
  );
  return fileName;
};

const downloadFileFromResponse = (m: MessageBox, response: AxiosResponse) => {
  const disposition = response.headers['content-disposition'];
  // 파일생성 실패인 경우 responseType: 'text' 로 실패 원인 확인
  if (disposition === undefined) {
    // eslint-disable-next-line no-alert
    m.alert('파일없음');
    return 'error';
  }
  const fileNameWithExt = extractDownloadFilenameWithExt(response);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileNameWithExt}`);
  document.body.appendChild(link);
  link.click();
  return '';
};

export const downloadFile = (
  args: Omit<CallAxiosArgs, 'responseType' | 'onSuccess'>,
) =>
  callAxios({
    ...args,
    responseType: 'blob',
    onSuccess: (_, res) => downloadFileFromResponse(args.m, res),
  });
